import { useEffect, useState } from 'react';
import logger from '@ayocore/platform/common/helpers/logger';
import {
  RepositoryCombobox,
  type RepositoryComboboxSlots,
} from '@ayocore/platform/client/components/RepositoryCombobox';
import type { RepoItem, RpcRepoClient } from '@ayocore/platform/server/rpc/types';
import { useRepositoryFilter } from './useRepositoryFilter';
import {
  WITHOUT_TOKEN,
  EMPTY_STATE,
  serialize,
  parse,
  applyMultiSelection,
  applySingleSelection,
  toMultiValue,
  toSingleValue,
  type FilterState,
} from './filterState';

/** Опция «пустое значение» (IS NULL): пункт «Без X» сверху списка, взаимоисключающий с выбором. */
export interface WithoutOption<TRepo> {
  /** Подпись пункта, напр. «Без ответственного». */
  label: string;
  /** Применение предиката к репозиторию таблицы, напр. `repo.filterWithoutOwner()`. */
  apply: (repo: TRepo) => TRepo;
}

interface BaseFilterConfig<TRepo, R extends RpcRepoClient> {
  /** Стабильный ключ фильтра (URL: `${tableId}_f_${name}`). */
  name: string;
  /**
   * Источник опций — вызывается как hook в теле компонента, возвращает
   * RPC-репозиторий (цепочку). Напр. `() => useUserRepository().onlyHumanActive().orderByName('asc')`.
   */
  source: () => R;
  placeholder?: string;
  /** Ширина таргета (инпута). */
  width?: number;
  /** Ширина дропдауна; по умолчанию = ширине таргета. Напр. инпут 250, раскрывашка 300. */
  dropdownWidth?: number;
  size?: string;
  searchable?: boolean;
  /**
   * multi: максимум пиллов в поле; сверх — компактный «+K» (как у Mantine
   * MultiSelect). Полезно в тулбаре таблицы, чтобы поле не разрасталось.
   */
  maxDisplayedValues?: number;
  /**
   * Поле записи для лейбла опции (по умолчанию авто: name/title/label).
   * ВНИМАНИЕ: для RPC-клиентов репозитория `RepoItem<R>` резолвится широко
   * (`Record<string, unknown>`), поэтому компилятор НЕ проверяет имя поля на
   * опечатки и не даёт автодополнения — сверяй со схемой вручную. Это самый
   * частый путь; `getOptionLabel` оставлен как escape-hatch.
   */
  displayField?: keyof RepoItem<R> & string;
  /**
   * Кастомный лейбл опции. По той же причине (`RepoItem<R>` = `Record<string,unknown>`
   * для RPC-клиента) `item.<field>` приходит как `unknown` — доставай значения
   * через `String(...)`. В большинстве случаев достаточно `displayField`.
   */
  getOptionLabel?: (item: RepoItem<R>) => string;
  /** Опция «Без X» (IS NULL): пункт сверху списка с sentinel-значением, XOR с выбором. */
  without?: WithoutOption<TRepo>;
  /** Доп. слоты дропдауна (top/firstItem/lastItem/bottom). */
  slots?: RepositoryComboboxSlots;
  /**
   * Каскад: сузить опции по значению ДРУГОГО фильтра (sibling) той же таблицы.
   * Читает соседа из URL по его `name` (siblingKey) → применяет `scope` к `source`;
   * при смене соседа опции пересчитываются, а выбор сбрасывается (если `clearOnChange`).
   * `siblingValue` — сырое URL-значение соседа (для single-фильтра это id строкой,
   * null — сосед снят). Напр. группа зависит от типа:
   *   `{ siblingKey: 'object_type',
   *      scope: (repo, typeId) => (typeId ? repo.filterByType(Number(typeId)) : repo),
   *      clearOnChange: true }`
   */
  dependsOn?: {
    siblingKey: string;
    scope: (repo: R, siblingValue: string | null) => R;
    clearOnChange?: boolean;
  };
  /**
   * Начальное значение, если в URL нет параметра фильтра (id строкой; для multi —
   * в сериализованном виде). Применяется на mount как восстановление (фильтр
   * активен, URL не пишется); значение из URL — в т.ч. выбранное пользователем —
   * всегда приоритетнее. Кейс: пикер в модалке, наследующий выбор родительской формы.
   */
  defaultValue?: string;
}

interface SingleFilterConfig<TRepo, R extends RpcRepoClient> extends BaseFilterConfig<TRepo, R> {
  mode?: 'single';
  /** Применение выбранного id к репозиторию, напр. `(repo, id) => repo.filterByManager(id)`. */
  by: (repo: TRepo, id: number) => TRepo;
}

interface MultiFilterConfig<TRepo, R extends RpcRepoClient> extends BaseFilterConfig<TRepo, R> {
  mode: 'multi';
  /** Применение выбранных id к репозиторию, напр. `(repo, ids) => repo.filterByOwners(ids)`. */
  by: (repo: TRepo, ids: number[]) => TRepo;
}

export type RepositoryFilterConfig<TRepo, R extends RpcRepoClient> =
  | SingleFilterConfig<TRepo, R>
  | MultiFilterConfig<TRepo, R>;

/**
 * createRepositoryFilter — декларативная фабрика реляционных select-фильтров (выпадающий
 * список / dropdown) для тулбара RepositoryTable: фильтр по связанной сущности — менеджер,
 * ответственный, партнёр, элемент справочника и т.п.
 *
 * Заменяет ручную склейку «загрузка опций из репозитория + Mantine Select/MultiSelect +
 * парсинг URL + вызов filter()» одной декларацией (~10 строк). UI (на RepositoryCombobox),
 * сериализацию URL, сброс пагинации и XOR-опцию «Без X» (фильтр IS NULL) берёт на себя
 * фабрика. Разработчик описывает лишь источник опций (`source`) и серверные методы
 * фильтрации (`by`, опц. `without`).
 *
 * Когда использовать:
 * - select-фильтр в RepositoryTable по значению из ДРУГОГО репозитория;
 * - режим single (один id) или multi (несколько id; пиллы + компактный «+K»);
 * - опционально пункт «Без X» (IS NULL), взаимоисключающий с выбором (`without`).
 *
 * Когда НЕ использовать:
 * - фильтр по собственному полю/enum/датам — обычный {@link useRepositoryFilter} + контрол;
 * - select в ФОРМЕ (не в фильтрах таблицы) — {@link RepositoryCombobox} / RepositorySelect.
 *
 * Возвращает компонент-фильтр для `RepositoryTable filters={[...]}` (та же сигнатура,
 * что отдаёт {@link useRepositoryFilter}).
 *
 * @example
 * // multi + «Без ответственного» (IS NULL), пиллы сворачиваются в «+K»
 * export const FilterByOwner = createRepositoryFilter<DealRepository, ReturnType<typeof useUserRepository>>({
 *   name: 'owner',
 *   mode: 'multi',
 *   placeholder: 'Ответственный...',
 *   maxDisplayedValues: 1,
 *   source: () => useUserRepository().onlyHumanActive().orderByName('asc'),
 *   displayField: 'name',
 *   by: (repo, ids) => repo.filterByOwners(ids),
 *   without: { label: 'Без ответственного', apply: repo => repo.filterWithoutOwner() },
 * });
 *
 * @example
 * // single (один менеджер), без «Без X»
 * export const FilterByManager = createRepositoryFilter<PartnerRepository, ReturnType<typeof useUserRepository>>({
 *   name: 'manager',
 *   placeholder: 'Менеджер...',
 *   source: () => useUserRepository().onlyHumanActive().orderByName('asc'),
 *   displayField: 'name',
 *   by: (repo, id) => repo.filterByManager(id),
 * });
 *
 * @see RepositoryCombobox — UI-виджет (single/multi/слоты), который рендерит фабрика
 * @see useRepositoryFilter — низкоуровневый контракт фильтра для нестандартных случаев
 */
export function createRepositoryFilter<TRepo, R extends RpcRepoClient>(
  config: RepositoryFilterConfig<TRepo, R>
) {
  const isMulti = config.mode === 'multi';

  return useRepositoryFilter<TRepo>(config.name, ({ filter, disabled, fromUrl, toUrl, readSiblingUrl, subscribe }) => {
    const dep = config.dependsOn;
    // Каскад: сырое значение соседнего фильтра из URL (для сужения опций через scope).
    const [siblingValue, setSiblingValue] = useState<string | null>(() =>
      dep ? readSiblingUrl(dep.siblingKey) : null
    );
    const baseRepo = config.source();
    const repo = dep ? dep.scope(baseRepo, siblingValue) : baseRepo;
    const [state, setState] = useState<FilterState>(EMPTY_STATE);

    // Применяет XOR-состояние к репозиторию таблицы.
    const applyState = (next: FilterState) => {
      if (next.without && config.without) {
        filter(config.without.apply);
        return;
      }
      const nums = next.ids.map(v => parseInt(v, 10)).filter(v => Number.isFinite(v));
      if (nums.length === 0) {
        filter(null);
        return;
      }
      if (config.mode === 'multi') {
        filter(repo => config.by(repo, nums));
      } else {
        filter(repo => config.by(repo, nums[0]));
      }
    };

    // Восстановление из URL на mount.
    useEffect(() => {
      try {
        const restored = parse(fromUrl(), isMulti);
        if (restored.without || restored.ids.length > 0) {
          setState(restored);
          applyState(restored);
        }
      } catch (e) {
        logger.warn(`createRepositoryFilter[${config.name}]: fromUrl failed`, e);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Единая точка изменения состояния: применяет фильтр + пишет URL.
    const commit = (next: FilterState) => {
      setState(next);
      applyState(next);
      toUrl(serialize(next, isMulti));
    };

    // Каскад (dependsOn): следим за соседним фильтром. При смене его значения опции
    // пересчитываются (repo = scope(source, siblingValue)), а собственный выбор
    // сбрасывается, если clearOnChange (сосед мог сделать текущий выбор невалидным).
    useEffect(() => {
      if (!dep) return;
      let current = siblingValue;
      const onSiblingChange = () => {
        const next = readSiblingUrl(dep.siblingKey);
        if (next === current) return;
        current = next;
        setSiblingValue(next);
        if (dep.clearOnChange && next != null) commit(EMPTY_STATE);
      };
      return subscribe(onSiblingChange);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // «Без X» — обычное значение: закреплённая сверху static-опция с sentinel-токеном.
    const staticOptions = config.without
      ? [{ value: WITHOUT_TOKEN, label: config.without.label }]
      : undefined;

    const shared = {
      repo,
      placeholder: config.placeholder,
      width: config.width,
      dropdownWidth: config.dropdownWidth,
      size: config.size ?? 'xs',
      searchable: config.searchable,
      maxDisplayedValues: config.maxDisplayedValues,
      displayField: config.displayField,
      getOptionLabel: config.getOptionLabel,
      disabled,
      staticOptions,
      slots: config.slots,
    };

    // Каскад: useRepositoryOptions не перезагружает опции при смене ссылки repo (грузит
    // по debouncedSearch), поэтому scoped-опции (dependsOn) подхватываются только свежим
    // маунтом — ремаунтим combobox по key при смене значения соседа. Без dependsOn key
    // === undefined, поведение не меняется.
    const comboKey = dep ? `dep:${siblingValue ?? ''}` : undefined;

    if (config.mode === 'multi') {
      return (
        <RepositoryCombobox<R>
          key={comboKey}
          {...shared}
          mode='multi'
          value={toMultiValue(state)}
          onChange={vals => commit(applyMultiSelection(state, vals))}
        />
      );
    }

    return (
      <RepositoryCombobox<R>
        key={comboKey}
        {...shared}
        value={toSingleValue(state)}
        onChange={value => commit(applySingleSelection(value))}
      />
    );
  }, { defaultValue: config.defaultValue });
}

export default createRepositoryFilter;
